import { useCallback, useEffect, useMemo, useState } from 'react';
import fetchGraphql, { GraphQLError } from './fetchGraphql';
import AbortController from 'abort-controller';

type CallbackOptions<Query> = {
    /** @deprecated will be phased out once migrated away from apollo */
    onCompleted?: (data: Query) => void;
    /** @deprecated will be phased out once migrated away from apollo */
    onError?: (errors?: GraphQLError[]) => void;
};

type UseLazyFetchReturn<Query, Variables> = {
    lazyFetch: (Variables?: Variables) => Promise<{
        data?: Query;
        errors?: GraphQLError[];
    }>;
    isPending: boolean;
    data?: Query;
    graphqlError?: GraphQLError[];
};

const useLazyFetchGraphql = <Query, Variables = Record<string, unknown>>(
    query: string,
    { onCompleted, onError }: CallbackOptions<Query> = {}
): UseLazyFetchReturn<Query, Variables> => {
    const [isPending, setIsPending] = useState(false);
    const [data, setData] = useState<Query>();
    const [graphqlError, setGraphqlError] = useState<GraphQLError[]>();

    const controller = useMemo(() => new AbortController(), []);

    useEffect(() => {
        return () => {
            controller.abort();
            setIsPending(false);
        };
    }, [controller]);

    const lazyFetch = useCallback(
        (variables: Variables | undefined) => {
            const fetchData = async () => {
                try {
                    setIsPending(true);

                    const options = { signal: controller.signal };

                    const { data, errors } = await fetchGraphql<
                        Query,
                        Variables
                    >(query, variables, options);

                    if (typeof onCompleted === 'function') {
                        // TODO: Allow onCompleted to be called without data
                        if (data) onCompleted(data);
                    }

                    if (errors) {
                        setGraphqlError(errors);

                        if (typeof onError === 'function') {
                            onError(errors);
                        }
                    }

                    setData(data || undefined);
                    setIsPending(false);

                    return { data, errors };
                } catch (error) {
                    if (
                        !controller.signal.aborted &&
                        typeof onError === 'function'
                    ) {
                        onError();
                    }
                }

                if (!controller.signal.aborted) {
                    setIsPending(false);
                }

                return { data, graphqlError };
            };

            return fetchData();
        },
        [controller.signal, data, graphqlError, onCompleted, onError, query]
    );

    return { lazyFetch, isPending, data, graphqlError };
};

export { useLazyFetchGraphql };
